/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel, RedirectRequest } from "@azure/msal-browser";
import { g_getCurrentCulture } from "./helpers/cultureHelper";

export interface IB2CPolicieNames {
    signUpSignIn: string;
    forgotPassword: string;
    editProfile: string;
}

export interface IB2CAuthority {
    authority: string;
}

export interface IB2CPolicies {
    names: IB2CPolicieNames;
    authorities: {
        signUpSignIn: RedirectRequest;
        forgotPassword: RedirectRequest;
        editProfile: RedirectRequest;
    };
    authorityDomain: string;
}

/**
 * The current culture of the page
 */
const _culture: string = g_getCurrentCulture();

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const g_b2cPolicies: IB2CPolicies = {
    names: {
        signUpSignIn: process.env.REACT_APP_B2C_POLICY_SUSI as string,
        forgotPassword: process.env.REACT_APP_B2C_POLICY_RESET as string,
        editProfile: process.env.REACT_APP_B2C_POLICY_EDIT as string,
    },
    authorities: {
        signUpSignIn: {
            authority: process.env
                .REACT_APP_B2C_POLICY_SUSI_AUTHORITY as string,
        } as RedirectRequest,
        forgotPassword: {
            authority: process.env
                .REACT_APP_B2C_POLICY_RESET_AUTHORITY as string,
        } as RedirectRequest,
        editProfile: {
            authority: process.env
                .REACT_APP_B2C_POLICY_EDIT_AUTHORITY as string,
        } as RedirectRequest,
    },
    authorityDomain: process.env.REACT_APP_B2C_AUTHORITY_DOMAIN as string,
};

export interface IMSALConfAuth {
    clientId: string; // This is the ONLY mandatory field that you need to supply.
    authority: string; // Choose SUSI as your default authority.
    knownAuthorities: string[]; // Mark your B2C tenant's domain as trusted.
    redirectUri: string; // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: string; // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: boolean; // If "tru
}

//separated out to get an interface
const _msalConfAuth: IMSALConfAuth = {
    clientId: process.env.REACT_APP_B2C_CLIENT_ID as string, // This is the ONLY mandatory field that you need to supply.
    authority: g_b2cPolicies.authorities.signUpSignIn.authority as string, // Choose SUSI as your default authority.
    knownAuthorities: [g_b2cPolicies.authorityDomain as string], // Mark your B2C tenant's domain as trusted.
    redirectUri: `${window.location.protocol}//${window.location.host}`, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: process.env.REACT_APP_B2C_REDIRECT_URI as string, // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const g_msalConfig = {
    auth: _msalConfAuth,
    cache: {
        cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const g_protectedResources = {
    apiCadGenerator: {
        endpoint: process.env.REACT_APP_CAD_ENDPOINT as string,
        scopes: {
            generator: [process.env.REACT_APP_CAD_SCOPE as string],
        },
    },
    apiAllDevices: {
        endpoint: process.env.REACT_APP_ALL_DEVICES_ENDPOINT as string,
        scopes: {
            generator: [process.env.REACT_APP_CAD_SCOPE as string],
        },
    },
    apiGetDescription: {
        endpoint: process.env.REACT_APP_DEVICE_DESCRIPTION_ENDPOINT as string,
        scopes: {
            generator: [process.env.REACT_APP_CAD_SCOPE as string],
        },
    },
    apiGetProjects: {
        endpoint: process.env.REACT_APP_ALL_PROJECTS_ENDPOINT as string,
        scopes: {
            generator: [process.env.REACT_APP_CAD_SCOPE as string],
        },
    },
    apiDeleteProject: {
        endpoint: process.env.REACT_APP_DELETE_PROJECT_ENDPOINT as string,
        scopes: {
            generator: [process.env.REACT_APP_CAD_SCOPE as string],
        },
    },
    apiGetProject: {
        endpoint: process.env.REACT_APP_GET_PROJECT_ENDPOINT as string,
        scopes: {
            generator: [process.env.REACT_APP_CAD_SCOPE as string],
        },
    },
    apiUpdateProfile: {
        endpoint: process.env.REACT_APP_UPDATE_PROFILE_ENDPOINT as string,
        scopes: {
            generator: [process.env.REACT_APP_CAD_SCOPE as string],
        },
    },
    apiIncrementStats: {
        endpoint: process.env.REACT_APP_INCREMENT_STATS_ENDPOINT as string,
        scopes: {
            generator: [process.env.REACT_APP_CAD_SCOPE as string],
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const g_loginRequest = {
    scopes: [...g_protectedResources.apiCadGenerator.scopes.generator],
    extraQueryParameters: {
        ui_locales: _culture,
    },
    state: _culture,
};
